import { useState, type CSSProperties, type ReactNode } from 'react';

import type { Envelope } from './client.ts';
import {
  COHORT_METRICS, COHORT_SUB_IDS, cohortDimensionLabel, cohortExport, cohortGrid, cohortPlayers, isMoneyMetric,
  type CohortAnchor, type CohortDimension, type CohortGranularity, type CohortMatrixWire, type CohortMetric,
  type CohortPeriod, type CohortPlayerWire, type CohortQuery, type CohortRowView,
} from './cohort.ts';
import { kitText } from './messages.ts';
import { formatCount, freshness } from './present.ts';
import {
  Callout, Empty, ErrorNotice, FilterBar, Loading, Panel, Select, Tab, TabBar, TableActions, TableScroll,
} from './ui.tsx';
import { useLoad } from './useLoad.ts';

/** The cell a drill opens, as the API names it. */
export interface CohortCellRef {
  readonly cohort: string;
  readonly checkpoint: string;
  /** Absent: every group. Empty: the unset group. */
  readonly group_key?: string;
}

/**
 * Rule Book 35.3a — the cohort report: a heat table of cohort by checkpoint,
 * with the anchor, checkpoints, cohort period, split and metric chosen on the
 * screen, an export of what is shown, and the players behind a cell.
 *
 * The console supplies the two loaders (they carry its own range, brand and
 * placement filters) and `refreshKey`, which changes whenever those do.
 */
export function CohortExplorer({
  load,
  loadPlayers,
  dimensions,
  refreshKey,
  now,
  filters,
  title = kitText('cohort.title'),
  fixedReason,
}: {
  load: (q: CohortQuery) => Promise<Envelope<CohortMatrixWire>>;
  loadPlayers?: (q: CohortQuery, cell: CohortCellRef) => Promise<Envelope<readonly CohortPlayerWire[]> & { has_more?: boolean }>;
  dimensions: readonly CohortDimension[];
  refreshKey: string;
  now: Date;
  filters?: ReactNode;
  title?: string;
  /**
   * Set when the report comes in one shape only (an affiliate whose brand
   * protects small cohorts): the reason, in the reader's language. The anchor,
   * cohort period, split and sub-ID filter are then not offered at all.
   */
  fixedReason?: string;
}) {
  const [anchor, setAnchor] = useState<CohortAnchor | undefined>(undefined);
  const [granularity, setGranularity] = useState<CohortGranularity | undefined>(undefined);
  const [period, setPeriod] = useState<CohortPeriod>('MONTH');
  const [dimension, setDimension] = useState<CohortDimension>('none');
  const [metric, setMetric] = useState<CohortMetric>('retention');
  const [currency, setCurrency] = useState<string | null>(null);
  const [drill, setDrill] = useState<{ row: CohortRowView; checkpoint: string } | null>(null);
  const [subIdIndex, setSubIdIndex] = useState('1');
  const [subIdDraft, setSubIdDraft] = useState('');
  const [subId, setSubId] = useState('');

  const query: CohortQuery = {
    ...(anchor ? { anchor } : {}),
    ...(granularity ? { granularity } : {}),
    period,
    dimension,
    ...(subId ? { sub_id: subId, sub_id_index: subIdIndex } : {}),
  };
  const loaded = useLoad(() => load(query), [refreshKey, anchor, granularity, period, dimension, subId, subIdIndex]);

  const shownAnchor = loaded.state === 'ready' ? loaded.value.data.anchor : anchor;
  // The server says so too, should a console not have asked first.
  const fixed = fixedReason ?? (loaded.state === 'ready' ? loaded.value.data.breakdowns_off_because : undefined);
  const shownGranularity = loaded.state === 'ready' ? loaded.value.data.granularity : granularity;
  const reset = () => setDrill(null);

  const controls = (
    <FilterBar>
      {fixed === undefined ? (
      <div className="field">
        <span className="field-label">{kitText('cohort.anchor')}</span>
        <TabBar aria-label={kitText('cohort.anchor')}>
          {(['REGISTRATION', 'FTD'] as const).map((a) => (
            <Tab key={a} selected={shownAnchor === a} onClick={() => { setAnchor(a); reset(); }}>
              {kitText(`cohort.anchor.${a}`)}
            </Tab>
          ))}
        </TabBar>
      </div>
      ) : null}
      <div className="field">
        <span className="field-label">{kitText('cohort.granularity')}</span>
        <TabBar aria-label={kitText('cohort.granularity')}>
          {(['MONTHS', 'DAYS'] as const).map((g) => (
            <Tab key={g} selected={shownGranularity === g} onClick={() => { setGranularity(g); reset(); }}>
              {kitText(`cohort.granularity.${g}`)}
            </Tab>
          ))}
        </TabBar>
      </div>
      {fixed === undefined ? (<>
      <label className="field">
        <span className="field-label">{kitText('cohort.period')}</span>
        <Select<CohortPeriod>
          value={period}
          onChange={(v) => { setPeriod(v); reset(); }}
          searchable={false}
          options={(['MONTH', 'WEEK'] as const).map((p) => ({ value: p, label: kitText(`cohort.period.${p}`) }))}
        />
      </label>
      <label className="field">
        <span className="field-label">{kitText('cohort.dimension')}</span>
        <Select<CohortDimension>
          value={dimension}
          onChange={(v) => { setDimension(v); reset(); }}
          searchable={false}
          options={dimensions.map((d) => ({ value: d, label: cohortDimensionLabel(d) }))}
        />
      </label>
      </>) : null}
      <label className="field">
        <span className="field-label">{kitText('cohort.metric')}</span>
        <Select<CohortMetric>
          value={metric}
          onChange={setMetric}
          searchable={false}
          options={COHORT_METRICS.map((m) => ({ value: m.value, label: kitText(m.label) }))}
        />
      </label>
      {fixed === undefined ? (
      <label className="field">
        <span className="field-label">{kitText('cohort.subIdFilter')}</span>
        <Select<string>
          value={subIdIndex}
          onChange={(v) => { setSubIdIndex(v); reset(); }}
          searchable={false}
          options={COHORT_SUB_IDS.map((_, i) => ({ value: String(i + 1), label: kitText('cohort.dim.subId', { n: String(i + 1) }) }))}
        />
        <input
          className="control"
          value={subIdDraft}
          placeholder={kitText('cohort.subIdValue')}
          aria-label={kitText('cohort.subIdValue')}
          onChange={(e) => setSubIdDraft(e.target.value)}
          onBlur={() => { setSubId(subIdDraft.trim()); reset(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { setSubId(subIdDraft.trim()); reset(); } }}
        />
      </label>
      ) : null}
      {filters}
    </FilterBar>
  );
  const fixedNote = fixed === undefined ? null : <Callout tone="note">{fixed}</Callout>;

  if (loaded.state !== 'ready') {
    return (
      <Panel title={title}>
        {controls}
        {fixedNote}
        {loaded.state === 'loading' ? <Loading rows={4} /> : <ErrorNotice error={loaded.error} />}
      </Panel>
    );
  }

  const data = loaded.value.data;
  const grid = cohortGrid(data, metric, currency);
  const metricLabel = kitText(COHORT_METRICS.find((m) => m.value === metric)!.label);
  const exported = cohortExport(grid, metricLabel);

  return (
    <>
      <Panel
        title={title}
        aside={
          <TableActions
            title={`${title}: ${metricLabel}`}
            headers={exported.headers}
            keys={exported.keys}
            rows={exported.rows}
            filename="cohorts"
          >
            <span className="updated">{freshness(loaded.value.data_as_of, now)}</span>
          </TableActions>
        }
      >
        {controls}
        {fixedNote}
        {isMoneyMetric(metric) && grid.currencies.length > 1 ? (
          <TabBar aria-label={kitText('cohort.currency')}>
            {grid.currencies.map((c) => (
              <Tab key={c} selected={grid.currency === c} onClick={() => setCurrency(c)}>{c}</Tab>
            ))}
          </TabBar>
        ) : null}
        {grid.notes.map((n) => <Callout key={n} tone="caution">{n}</Callout>)}
        {grid.rows.length === 0 ? (
          <Empty>{kitText('cohort.empty')}</Empty>
        ) : (
          <TableScroll>
            <table className="cohort-table">
              <thead>
                <tr>
                  <th className="date">{kitText('cohort.col.cohort')}</th>
                  {grid.grouped ? <th>{cohortDimensionLabel(data.dimension)}</th> : null}
                  <th className="num">{kitText('cohort.col.players')}</th>
                  {grid.checkpoints.map((k) => <th key={k} className="num">{k}</th>)}
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((r) => (
                  <tr key={r.key}>
                    <td className="date faint">{r.cohortLabel}</td>
                    {grid.grouped ? <td>{r.groupLabel}</td> : null}
                    <td className="num">{r.players === null ? kitText('cohort.withheld') : formatCount(r.players)}</td>
                    {r.cells.map((c) => {
                      const style = c.heat === null ? undefined : ({ '--heat': String(c.heat) } as CSSProperties);
                      const selected = drill?.row.key === r.key && drill.checkpoint === c.checkpoint;
                      const cls = `num cohort-cell${c.heat === null ? '' : ' has-heat'}${c.complete ? '' : ' is-incomplete'}${selected ? ' is-selected' : ''}`;
                      return (
                        <td key={c.checkpoint} className={cls} style={style} {...(c.title ? { title: c.title } : {})}>
                          {/* A pooled row spans many cohorts, and a withheld one has no players to list. */}
                          {loadPlayers && c.text !== '' && !r.pooled ? (
                            <button type="button" className="cohort-cell-button" onClick={() => setDrill({ row: r, checkpoint: c.checkpoint })}>
                              {c.text}
                            </button>
                          ) : c.text}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
        {grid.commissionOtherLines > 0 && metric === 'commission' ? (
          <Callout tone="note">{kitText('cohort.otherCurrencyLines', { n: String(grid.commissionOtherLines) })}</Callout>
        ) : null}
        <Callout tone="note">{kitText('cohort.incompleteNote')}</Callout>
        <Callout tone="note">{kitText('cohort.activityNote')}</Callout>
        {loadPlayers ? <p className="faint">{kitText('cohort.drillHint')}</p> : null}
      </Panel>
      {drill && loadPlayers ? (
        <CohortPlayersPanel
          key={`${drill.row.key}|${drill.checkpoint}|${refreshKey}`}
          load={() => loadPlayers(
            { ...query, anchor: data.anchor, granularity: data.granularity },
            {
              cohort: drill.row.cohort,
              checkpoint: drill.checkpoint,
              ...(drill.row.groupKey === undefined ? {} : { group_key: drill.row.groupKey ?? '' }),
            },
          )}
          title={kitText('cohort.drill.title', {
            cohort: drill.row.groupLabel ? `${drill.row.cohortLabel} · ${drill.row.groupLabel}` : drill.row.cohortLabel,
            checkpoint: drill.checkpoint,
          })}
          onClose={() => setDrill(null)}
        />
      ) : null}
    </>
  );
}

function CohortPlayersPanel({
  load,
  title,
  onClose,
}: {
  load: () => Promise<Envelope<readonly CohortPlayerWire[]> & { has_more?: boolean }>;
  title: string;
  onClose: () => void;
}) {
  const loaded = useLoad(load, []);
  const close = <button type="button" className="copy btn-sm" onClick={onClose}>{kitText('cohort.drill.close')}</button>;
  if (loaded.state !== 'ready') {
    return (
      <Panel title={title} aside={close}>
        {loaded.state === 'loading' ? <Loading rows={3} /> : <ErrorNotice error={loaded.error} />}
      </Panel>
    );
  }
  const rows = cohortPlayers(loaded.value.data);
  const headers = [
    kitText('cohort.col.player'), kitText('cohort.col.registered'), kitText('cohort.col.firstDeposit'),
    kitText('cohort.col.country'), kitText('cohort.metric.depositCount'), kitText('cohort.metric.deposits'),
    kitText('cohort.metric.withdrawals'), kitText('cohort.metric.ggr'), kitText('cohort.metric.ngr'),
    kitText('cohort.metric.commission'), kitText('cohort.col.active'),
  ];
  return (
    <Panel
      title={title}
      aside={
        <TableActions
          title={title}
          headers={headers}
          keys={['player', 'registered', 'first_deposit', 'country', 'deposit_count', 'deposits', 'withdrawals',
            'ggr', 'ngr', 'commission', 'active']}
          rows={rows.map((r) => [r.playerRef, r.registered, r.firstDeposit, r.country, r.depositCount, r.deposits,
            r.withdrawals, r.ggr, r.ngr, r.commission, r.active])}
          filename="cohort-players"
        >
          {close}
        </TableActions>
      }
    >
      {rows.length === 0 ? (
        <Empty>{kitText('cohort.empty')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>{headers.map((h, i) => <th key={h} className={i >= 4 && i <= 9 ? 'num' : undefined}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.playerRef}>
                  <td className="mono">{r.playerRef}</td>
                  <td className="date">{r.registered}</td>
                  <td className="date">{r.firstDeposit}</td>
                  <td>{r.country}</td>
                  <td className="num">{r.depositCount}</td>
                  <td className="num money">{r.deposits}</td>
                  <td className="num money">{r.withdrawals}</td>
                  <td className="num money">{r.ggr}</td>
                  <td className="num money">{r.ngr}</td>
                  <td className="num money">{r.commission}</td>
                  <td>{r.active}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {loaded.value.has_more ? <p className="faint">{kitText('cohort.drill.more')}</p> : null}
    </Panel>
  );
}
